import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Warning,
  Message,
  AttachFile,
  Info,
  Gavel,
  Upload,
  ReplyAll
} from '@mui/icons-material';
import DialogCloseButton from '../common/DialogCloseButton';

/**
 * Contractor Dispute Manager
 * Allows contractors to view disputes against them, respond with evidence and messages
 */
const ContractorDisputeManager = ({ apiClient }) => {
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseOpen, setResponseOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  // Response form
  const [responseForm, setResponseForm] = useState({
    message: ''
  });

  // Evidence form
  const [evidenceForm, setEvidenceForm] = useState({
    type: 'photo',
    description: '',
    file: null
  });

  // Messages
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    loadDisputesAgainstMe();
  }, []);

  const loadDisputesAgainstMe = async () => {
    setLoading(true);
    try {
      // Get escrows where I'm the contractor
      const escrowsResponse = await apiClient.get('/escrow/my-contracts');
      if (escrowsResponse.data.success) {
        const escrows = escrowsResponse.data.data;
        
        // Get disputes for these escrows
        const disputesPromises = escrows.map(escrow =>
          apiClient.get(`/escrow/${escrow.id}/disputes`).catch(() => ({ data: { data: [] } }))
        );
        
        const disputesResponses = await Promise.all(disputesPromises);
        const allDisputes = disputesResponses.flatMap(r => r.data.data || []);
        
        setDisputes(allDisputes);
      }
    } catch (error) {
      console.error('Failed to load disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedDispute || !responseForm.message.trim()) return;

    try {
      await apiClient.post(`/disputes/${selectedDispute.id}/messages`, {
        message: responseForm.message,
        messageType: 'response'
      });

      alert('Response submitted successfully');
      setResponseOpen(false);
      setResponseForm({ message: '' });
      loadDisputesAgainstMe();
    } catch (error) {
      alert('Failed to submit response: ' + error.message);
    }
  };

  const handleAddEvidence = async () => {
    if (!selectedDispute || !evidenceForm.file) return;

    try {
      // Upload file
      const formData = new FormData();
      formData.append('file', evidenceForm.file);
      formData.append('disputeId', selectedDispute.id);

      const uploadResponse = await apiClient.post('/uploads/evidence', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = uploadResponse.data.url;

      // Add evidence
      await apiClient.post(`/disputes/${selectedDispute.id}/evidence`, {
        evidenceType: evidenceForm.type,
        fileUrl: fileUrl,
        description: evidenceForm.description
      });

      alert('Evidence added successfully');
      setEvidenceOpen(false);
      setEvidenceForm({ type: 'photo', description: '', file: null });
    } catch (error) {
      alert('Failed to add evidence: ' + error.message);
    }
  };

  const loadMessages = async (disputeId) => {
    try {
      const response = await apiClient.get(`/disputes/${disputeId}/messages`);
      if (response.data.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedDispute || !newMessage.trim()) return;

    try {
      await apiClient.post(`/disputes/${selectedDispute.id}/messages`, {
        message: newMessage,
        messageType: 'message'
      });

      setNewMessage('');
      loadMessages(selectedDispute.id);
    } catch (error) {
      alert('Failed to send message: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'warning',
      investigating: 'info',
      escalated: 'error',
      mediation: 'secondary',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[severity] || 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const renderDisputeCard = (dispute) => (
    <Card key={dispute.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6">
              {dispute.project_name || `Project ${dispute.project_id?.substring(0, 8)}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Amount at stake: {formatCurrency(dispute.total_amount)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Chip 
              label={dispute.status}
              color={getStatusColor(dispute.status)}
              size="small"
              sx={{ mb: 1 }}
            />
            {dispute.severity_level && (
              <Chip 
                label={`Severity: ${dispute.severity_level}`}
                color={getSeverityColor(dispute.severity_level)}
                size="small"
              />
            )}
          </Box>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Client's Complaint:</strong> {dispute.dispute_reason?.replace('_', ' ')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {dispute.description}
          </Typography>
        </Alert>

        {dispute.resolution && (
          <Alert severity={dispute.resolution_type?.includes('refund') ? 'error' : 'success'} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Resolution:</strong> {dispute.resolution}
            </Typography>
            {dispute.resolution_type && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Type: {dispute.resolution_type.replace('_', ' ')}
              </Typography>
            )}
          </Alert>
        )}

        <Typography variant="caption" display="block" color="textSecondary" gutterBottom>
          Raised on: {formatDate(dispute.created_at)}
        </Typography>

        {dispute.sla_deadline && (
          <Typography variant="caption" display="block" color="error" gutterBottom>
            <Warning sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
            SLA Deadline: {formatDate(dispute.sla_deadline)}
          </Typography>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ReplyAll />}
            onClick={() => {
              setSelectedDispute(dispute);
              setResponseOpen(true);
            }}
            disabled={dispute.status === 'resolved' || dispute.status === 'closed'}
          >
            Respond
          </Button>
          <Button
            size="small"
            startIcon={<Message />}
            onClick={() => {
              setSelectedDispute(dispute);
              loadMessages(dispute.id);
              setMessagesOpen(true);
            }}
          >
            Messages
          </Button>
          <Button
            size="small"
            startIcon={<AttachFile />}
            onClick={() => {
              setSelectedDispute(dispute);
              setEvidenceOpen(true);
            }}
            disabled={dispute.status === 'resolved' || dispute.status === 'closed'}
          >
            Add Evidence
          </Button>
        </Box>

        {dispute.fraud_score >= 30 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              This dispute involves fraud detection concerns. Please provide clear evidence to support your case.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
        Disputes Against My Work
      </Typography>

      {disputes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            You have {disputes.filter(d => d.status !== 'resolved' && d.status !== 'closed').length} active dispute(s). 
            Please respond promptly to avoid negative impacts on your reputation and payments.
          </Typography>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : disputes.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No disputes filed against your work
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Keep up the great work! Maintain quality to avoid disputes.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {disputes.map(renderDisputeCard)}
        </Box>
      )}

      {/* Response Dialog */}
      <Dialog open={responseOpen} onClose={() => setResponseOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setResponseOpen(false)} />
        <DialogTitle>
          <ReplyAll sx={{ mr: 1, verticalAlign: 'middle' }} />
          Respond to Dispute
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Provide a clear, professional response. Include details about:
            <ul>
              <li>What work was completed</li>
              <li>Any challenges or changes agreed upon</li>
              <li>Evidence supporting your position</li>
            </ul>
          </Alert>

          {selectedDispute && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Client's Issue:</strong> {selectedDispute.description}
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Your Response"
            value={responseForm.message}
            onChange={(e) => setResponseForm({ message: e.target.value })}
            placeholder="Explain your side of the situation..."
            required
          />

          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            Your response will be visible to the client and our resolution team.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitResponse}
            disabled={!responseForm.message.trim()}
          >
            Submit Response
          </Button>
        </DialogActions>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={messagesOpen} onClose={() => setMessagesOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setMessagesOpen(false)} />
        <DialogTitle>Dispute Conversation</DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
            {messages.length === 0 ? (
              <Alert severity="info">No messages yet.</Alert>
            ) : (
              messages.map((msg, index) => (
                <React.Fragment key={msg.id}>
                  <ListItem 
                    alignItems="flex-start"
                    sx={{ 
                      bgcolor: msg.is_internal ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">
                            {msg.sender_name}
                            {msg.is_internal && <Chip label="Internal" size="small" sx={{ ml: 1 }} />}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(msg.created_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {msg.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < messages.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Your Message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessagesOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            startIcon={<Message />}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evidence Upload Dialog */}
      <Dialog open={evidenceOpen} onClose={() => setEvidenceOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setEvidenceOpen(false)} />
        <DialogTitle>Add Evidence</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              select
              label="Evidence Type"
              value={evidenceForm.type}
              onChange={(e) => setEvidenceForm({ ...evidenceForm, type: e.target.value })}
              sx={{ mb: 2 }}
            >
              <MenuItem value="photo">Photo</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="document">Document</MenuItem>
              <MenuItem value="screenshot">Screenshot</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={evidenceForm.description}
              onChange={(e) => setEvidenceForm({ ...evidenceForm, description: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Describe this evidence..."
            />

            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<Upload />}
            >
              {evidenceForm.file ? evidenceForm.file.name : 'Choose File'}
              <input
                type="file"
                hidden
                onChange={(e) => setEvidenceForm({ ...evidenceForm, file: e.target.files[0] })}
                accept="image/*,video/*,.pdf,.doc,.docx"
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddEvidence}
            disabled={!evidenceForm.file}
            startIcon={<AttachFile />}
          >
            Add Evidence
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractorDisputeManager;

